import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pb } from '../lib/pb';
import { errorMessage } from '../lib/errors';
import { toast } from '../store/store';
import { loadGameData, useData } from '../data/dataStore';
import { defaultVisibility, humanize, settingKey } from '../data/schema';
import type { DataFieldRecord, FieldDef, Visibility } from '../data/types';

const VISIBILITIES: Visibility[] = ['edit', 'readonly', 'hidden'];
/** How a text field is filled in: free text, suggestions from the values Unity uses, or only those values. */
/** '' = automatic (see pickerFor in data/schema.ts): map places and code-bound names get a picker. */
const PICKERS = ['', 'free', 'vocab', 'vocab_strict', 'map:zone', 'map:poi', 'map:quest_location', 'map:npc'];
const isText = (f: FieldDef) => f.kind === 'string' || (f.kind === 'list' && f.element?.kind === 'string');

/**
 * Admin → Fields: what designers see of each class. Settings belong to the class that DECLARES a
 * field, so e.g. ItemData.sellPrice is set once for items, food and equipment. Fields that appeared
 * in the last Unity sync are marked "new" until reviewed here.
 */
export function FieldSettingsAdmin() {
  const { t } = useTranslation();
  const classes = useData((s) => s.classes);
  const settings = useData((s) => s.settings);
  const loaded = useData((s) => s.loaded);
  const [owner, setOwner] = useState('');
  const [onlyNew, setOnlyNew] = useState(false);

  useEffect(() => {
    if (!loaded) void loadGameData();
  }, [loaded]);

  const owners = useMemo(() => {
    const newCount: Record<string, number> = {};
    for (const s of Object.values(settings))
      if (s.isNew && !s.removed) newCount[s.owner] = (newCount[s.owner] ?? 0) + 1;
    return Object.values(classes)
      .filter((c) => c.kind !== 'enum' && !c.missing && (c.fields?.length ?? 0) > 0)
      .map((c) => ({ name: c.className, label: c.label, kind: c.kind, isNew: newCount[c.className] ?? 0 }))
      .filter((c) => !onlyNew || c.isNew > 0)
      .sort((a, b) => (a.kind === b.kind ? a.label.localeCompare(b.label) : a.kind === 'data' ? -1 : 1));
  }, [classes, settings, onlyNew]);

  const current = owners.find((o) => o.name === owner) ?? owners[0];
  const cls = current ? classes[current.name] : undefined;
  const fields = (cls?.fields ?? []).filter(
    (f) => !onlyNew || settings[settingKey(cls!.className, f.name)]?.isNew,
  );

  const markAllSeen = async () => {
    if (!cls) return;
    try {
      for (const f of cls.fields ?? []) {
        const s = settings[settingKey(cls.className, f.name)];
        if (s?.isNew) await pb.collection('data_fields').update(s.id, { isNew: false });
      }
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  // Visibility the current rules would give each field (e.g. after the tuning-name rule arrived in D4).
  const suggestions = (cls?.fields ?? [])
    .map((f) => ({ f, s: settings[settingKey(cls!.className, f.name)], want: defaultVisibility(f) }))
    .filter((x) => x.s && x.s.visibility !== x.want);

  const applySuggestions = async () => {
    const list = suggestions
      .map(
        (x) =>
          `${x.f.name}: ${t(`admin.fields.visibility.${x.s!.visibility}`)} → ${t(`admin.fields.visibility.${x.want}`)}`,
      )
      .join('\n');
    if (!confirm(t('admin.fields.confirmSuggest', { count: suggestions.length }) + '\n\n' + list)) return;
    try {
      for (const x of suggestions)
        await pb.collection('data_fields').update(x.s!.id, { visibility: x.want, isNew: false });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  if (owners.length === 0 && !onlyNew) return <p className="muted">{t('data.empty')}</p>;
  return (
    <div className="admin-fields">
      <p className="muted small">{t('admin.fields.help')}</p>
      <label className="check-row">
        <input type="checkbox" checked={onlyNew} onChange={() => setOnlyNew(!onlyNew)} />
        {t('admin.fields.onlyNew')}
      </label>
      <select value={current?.name ?? ''} onChange={(e) => setOwner(e.target.value)}>
        {owners.map((o) => (
          <option key={o.name} value={o.name}>
            {o.label}
            {o.kind === 'struct' ? ` (${t('admin.fields.nested')})` : ''}
            {o.isNew ? ` — ${t('admin.fields.newCount', { count: o.isNew })}` : ''}
          </option>
        ))}
      </select>
      {cls && (
        <>
          <div className="row">
            <span className="mono small muted ellipsis">{cls.className}</span>
            {current.isNew > 0 && (
              <button className="link small" onClick={() => void markAllSeen()}>
                {t('admin.fields.markAllSeen')}
              </button>
            )}
            {suggestions.length > 0 && (
              <button
                className="link small"
                title={t('admin.fields.suggestHelp')}
                onClick={() => void applySuggestions()}
              >
                {t('admin.fields.suggest', { count: suggestions.length })}
              </button>
            )}
          </div>
          {fields.map((f) => (
            <FieldSettingCard key={f.name} field={f} setting={settings[settingKey(cls.className, f.name)]} />
          ))}
        </>
      )}
    </div>
  );
}

function FieldSettingCard({ field, setting }: { field: FieldDef; setting: DataFieldRecord | undefined }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!setting) return null;

  const save = async (patch: Partial<DataFieldRecord>) => {
    try {
      await pb.collection('data_fields').update(setting.id, { ...patch, isNew: false });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  const text = (k: 'labelSv' | 'labelEn' | 'helpSv' | 'helpEn' | 'section', multiline = false) => {
    const props = {
      defaultValue: setting[k],
      onBlur: (e: { target: { value: string } }) => {
        if (e.target.value !== setting[k]) void save({ [k]: e.target.value });
      },
    };
    return (
      <label>
        {t(`admin.fields.${k}`)}
        {multiline ? <textarea rows={2} {...props} /> : <input {...props} />}
      </label>
    );
  };

  return (
    <div className={`card field-card${setting.visibility === 'hidden' ? ' dim' : ''}`}>
      <div className="row">
        <span className="ellipsis">
          <strong>{setting.labelSv || field.label || humanize(field.name)}</strong>{' '}
          <span className="mono small muted">{field.name}</span>
        </span>
        {setting.isNew && <span className="chip new">{t('data.newField')}</span>}
        <span className="spacer" />
        <span className="muted small">{kindText(field)}</span>
      </div>
      <div className="row">
        <select
          className="small"
          value={setting.visibility}
          onChange={(e) => void save({ visibility: e.target.value as Visibility })}
        >
          {VISIBILITIES.map((v) => (
            <option key={v} value={v}>
              {t(`admin.fields.visibility.${v}`)}
            </option>
          ))}
        </select>
        {setting.isNew && (
          <button className="link small" onClick={() => void save({})}>
            {t('admin.fields.markSeen')}
          </button>
        )}
        <span className="spacer" />
        <button className="link small" onClick={() => setOpen(!open)}>
          {open ? t('common.close') : t('admin.fields.labels')}
        </button>
      </div>
      {field.tooltip && <div className="muted small">{field.tooltip}</div>}
      {open && (
        <div className="prop-grid">
          {text('labelSv')}
          {text('labelEn')}
          <div className="span2">{text('helpSv', true)}</div>
          <div className="span2">{text('helpEn', true)}</div>
          {text('section')}
          {isText(field) && (
            <label>
              {t('admin.fields.picker')}
              <select value={setting.picker} onChange={(e) => void save({ picker: e.target.value })}>
                {PICKERS.map((p) => (
                  <option key={p} value={p}>
                    {t(`admin.fields.pickers.${(p || 'auto').replace(':', '_')}`)}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}
    </div>
  );
}

function kindText(f: FieldDef): string {
  const inner = (x: FieldDef | undefined): string =>
    !x
      ? '?'
      : x.kind === 'list'
        ? `list<${inner(x.element)}>`
        : x.type || x.assetType
          ? `${x.kind} ${(x.type || x.assetType)!.split('.').pop()}`
          : x.kind;
  return inner(f);
}
